import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api/client.js';
import type { RankingEntry, RankingResponse, RankingScope } from '../../lib/api/types.js';
import { LoadingState } from '../../ui/components/LoadingState/LoadingState.tsx';
import { useAsyncData } from '../../ui/components/LoadingState/useAsyncData.ts';
import { OfflineState } from '../../ui/components/OfflineState/OfflineState.tsx';
import { SegmentedNav } from '../../ui/components/Navigation.tsx';
import { Avatar } from '../../ui/primitives/Avatar.tsx';
import { Surface } from '../../ui/primitives/Surface.tsx';
import { Text } from '../../ui/primitives/Text.tsx';
import styles from './ranking.module.css';

/**
 * `/ranking` — "how is my week going, next to theirs?"
 *
 * THE FRIENDS TAB OPENS FIRST, and that is the whole reason the follow graph
 * exists. A global board among forty learners is discouraging for
 * thirty-seven of them; the same week among five people from a class or a
 * roda is a real contest. The server already defaults to it — this screen
 * agrees with the server rather than deciding again.
 *
 * THE SCREEN SAYS ONE THING ABOUT THE WEEK: it zeroes on Monday. The board is
 * cut in `America/Sao_Paulo` for everybody — it has to be, or two learners'
 * weeks would not be the same week — but that is a fact about the server's
 * arithmetic, not something a person came here to read. THEY CAME TO SEE WHERE
 * THEY STAND. The zone is answerable when somebody asks; it is not worth a
 * line above the board.
 *
 * NOBODY IS SHOWN LOSING BY DEFAULT. A learner with no points this week is not
 * given a rank at the bottom of the list — they are told the week is open and
 * pointed at practice, because "unplaced" and "last" are different things to
 * say to somebody who simply has not started yet.
 *
 * THE PODIUM IS ALWAYS THREE PLACES, filled or not. A board holding one name
 * looks broken; the same board with second and third standing open reads as a
 * contest nobody has claimed yet, which is the truth of it. See `OpenPlace`.
 */
export function RankingPage() {
  const [scope, setScope] = useState<RankingScope>('friends');

  const { status, data: board, showLoading, refreshing, reload } =
    useAsyncData<RankingResponse | null>(() => api.ranking(scope), null, [scope]);

  if (status === 'loading') {
    return showLoading ? <LoadingState label="Carregando o ranking" /> : null;
  }

  if (status === 'unavailable' || !board) {
    return (
      <div className={styles.page}>
        <OfflineState
          title="Não foi possível carregar o ranking"
          onRetry={() => reload({ force: true })}
        />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.intro}>
        <Text as="h1" variant="display-xl">
          Sua semana
        </Text>
        <Text as="p" variant="body" tone="muted">
          Zera toda segunda.
        </Text>
      </header>

      {/*
        The one pill, § 14. Two tabs is the whole navigation this screen has:
        the contest you are in, and the one everybody is in.
      */}
      <SegmentedNav
        label="Quem entra no ranking"
        items={[
          { id: 'friends', label: 'Quem eu sigo', current: scope === 'friends' },
          { id: 'global', label: 'Todos', current: scope === 'global' },
        ]}
        onSelect={(id) => setScope(id as RankingScope)}
      />

      <section aria-label="Classificação da semana" aria-busy={refreshing || undefined}>
        <Surface layer={1} radius="2xl" inset>
          {board.entries.length === 0 ? (
            <EmptyBoard scope={board.scope} />
          ) : (
            <ol className={styles.board}>
              {board.entries.map((entry) => (
                <Row key={entry.learner.id} entry={entry} />
              ))}
              {openPlaces(board.entries).map((rank) => (
                <OpenPlace key={`open-${rank}`} rank={rank} />
              ))}
            </ol>
          )}
        </Surface>
      </section>

      {board.you === null && board.entries.length > 0 ? (
        <Text as="p" variant="body" tone="muted">
          Você ainda não pontuou. <Link to="/praticar">Pratique para entrar.</Link>
        </Text>
      ) : null}
    </div>
  );
}

/**
 * One place on the board.
 *
 * IT IS BUILT TO BE READ AT A GLANCE, the way a leaderboard is: the place, the
 * face, the name in the heavy weight, and the score at the right edge on its
 * own tabular figures. Three columns and nothing else on the line — no
 * secondary text under the name, no chevron, no metadata — because the eye
 * runs DOWN a board's two outer columns and anything else on a row is
 * something it has to step over.
 *
 * THE TOP THREE CARRY A DISC, and it descends: the accent at three strengths,
 * flat. Not gold, silver and bronze — a medal palette would be a fourth
 * colour language on a screen that already has the product's own (§ 4), and
 * the ladder reads as a podium without one. Flat rather than gradient because
 * a 44px disc is too small to travel a ramp; ranking.module.css records why.
 *
 * The learner's own line is MARKED, never moved: a board that lifted you out
 * of the order would stop being a ranking.
 */
function Row({ entry }: { entry: RankingEntry }) {
  return (
    <li className={entry.you ? `${styles.row} ${styles.you}` : styles.row}>
      <span className={styles.rank} data-place={entry.rank <= PODIUM_PLACES ? entry.rank : undefined}>
        <Text as="span" variant="subheading">
          {entry.rank}
        </Text>
      </span>

      {/*
        Your own line goes to your own profile, everybody else's to theirs —
        the same page either way, since one shape serves both.
      */}
      <Link to={entry.you ? '/perfil' : `/perfil/${entry.learner.id}`} className={styles.learner}>
        <Avatar name={entry.learner.name} image={entry.learner.image} size="md" />
        <span className={styles.name}>
          <Text as="span" variant="subheading">
            {entry.learner.name}
          </Text>
          {entry.you ? (
            <Text as="span" variant="body-sm" tone="muted">
              você
            </Text>
          ) : null}
        </span>
      </Link>

      <span className={styles.points}>
        <Text as="span" variant="subheading" tone="muted">
          {entry.points} pts
        </Text>
      </span>
    </li>
  );
}

/** How many places the podium has — the same three the profile counts. */
const PODIUM_PLACES = 3;

/**
 * The podium places nobody has taken, as ranks.
 *
 * COUNTED FROM THE LAST REAL RANK, never from the number of rows, because the
 * board shares a rank between ties: two learners level on points are both
 * second, and the place standing open after them is fourth — which is outside
 * the podium, so nothing is drawn. Counting rows instead would invent a third
 * place that the next learner to score cannot actually take.
 *
 * Empty in the one case where an open place would be the wrong thing to show:
 * an empty board, where `EmptyBoard` already says what is going on.
 */
function openPlaces(entries: readonly RankingEntry[]): number[] {
  const last = entries[entries.length - 1];
  if (!last) return [];

  const places: number[] = [];
  for (let rank = last.rank + 1; rank <= PODIUM_PLACES; rank += 1) places.push(rank);
  return places;
}

/**
 * A place on the podium with nobody in it.
 *
 * It is a row of the same list, at the same rhythm, with the name replaced by
 * an invitation rather than by a grey bar — "Em aberto" is a statement about
 * the week, and a learner one place below it can read it as the thing they are
 * playing for. It is not a person, so it is not a link and carries no avatar
 * to imply one.
 */
function OpenPlace({ rank }: { rank: number }) {
  return (
    <li className={`${styles.row} ${styles.open}`}>
      <span className={styles.rank}>
        <Text as="span" variant="subheading">
          {rank}
        </Text>
      </span>

      <span className={styles.learner}>
        <span className={styles.openMark} aria-hidden="true" />
        <Text as="span" variant="subheading" tone="muted">
          Em aberto
        </Text>
      </span>

      <span className={styles.points}>
        <Text as="span" variant="subheading" tone="muted">
          —
        </Text>
      </span>
    </li>
  );
}

/**
 * The empty friends board is the common case on a new account, and it is a
 * prompt rather than a failure: there is nobody to compare with yet, and the
 * way out is to follow somebody.
 */
function EmptyBoard({ scope }: { scope: RankingScope }) {
  if (scope === 'friends') {
    return (
      <div className={styles.empty}>
        <Text as="p" variant="body">
          Ninguém que você segue pontuou nesta semana — nem você.
        </Text>
        <Text as="p" variant="body-sm" tone="muted">
          <Link to="/perfil">Compartilhe seu convite</Link> com quem toca com você.
        </Text>
      </div>
    );
  }

  return (
    <div className={styles.empty}>
      <Text as="p" variant="body">
        Ninguém pontuou nesta semana ainda.
      </Text>
      <Text as="p" variant="body-sm" tone="muted">
        <Link to="/praticar">Pratique</Link> e a semana começa por você.
      </Text>
    </div>
  );
}
